// ─────────────────────────────────────────────────────────────────────────────
// Languages — single source of truth for all language/dialect data
// ─────────────────────────────────────────────────────────────────────────────

export interface Dialect {
  code: string;
  label: string;
}

export interface Language {
  code: string;
  label: string;
  dialects: Dialect[];
}

export const LANGUAGES: Language[] = [
  {
    code: "arabic",
    label: "Arabic",
    dialects: [
      { code: "egyptian",   label: "Egyptian Arabic" },
      { code: "gulf",       label: "Gulf Arabic" },
      { code: "levantine",  label: "Levantine Arabic" },
      { code: "iraqi",      label: "Iraqi Arabic" },
      { code: "maghrebi",   label: "Maghrebi Arabic" },
      { code: "sudanese",   label: "Sudanese Arabic" },
      { code: "msa",        label: "Modern Standard Arabic (Fosha)" },
    ],
  },
  {
    code: "english",
    label: "English",
    dialects: [
      { code: "american", label: "American English" },
      { code: "british",  label: "British English" },
    ],
  },
  {
    code: "french",
    label: "French",
    dialects: [],
  },
  {
    code: "spanish",
    label: "Spanish",
    dialects: [],
  },
  {
    code: "german",
    label: "German",
    dialects: [],
  },
  {
    code: "italian",
    label: "Italian",
    dialects: [],
  },
  {
    code: "turkish",
    label: "Turkish",
    dialects: [],
  },
];

/** Sentinel value for the free-text "Other" language option */
export const OTHER_LANGUAGE_VALUE = "other";

/** { value, label } pairs — for <select> / MultiSelectField */
export const LANGUAGE_OPTIONS = [
  ...LANGUAGES.map(({ code, label }) => ({
    value: code,
    label,
  })),
  { value: OTHER_LANGUAGE_VALUE, label: "Other" },
];

/** Plain string labels — for CheckGroup / plain-string consumers */
export const LANGUAGE_LABELS = LANGUAGES.map(({ label }) => label);

/** Look up a Language by code */
export const getLanguage = (code: string): Language | undefined =>
  LANGUAGES.find((l) => l.code === code);

/** Get dialect options for a given language code */
export const getDialects = (languageCode: string): Dialect[] =>
  getLanguage(languageCode)?.dialects ?? [];

/** Dialect { value, label } pairs for a given language code */
export const getDialectOptions = (languageCode: string) =>
  getDialects(languageCode).map(({ code, label }) => ({ value: code, label }));

// ─────────────────────────────────────────────────────────────────────────────
// The talents table has no separate `dialects` column — dialect codes are
// stored as extra entries in the same `languages` json array. These two
// helpers translate between that wire format and the { languages, dialects }
// shape the UI works with.
// ─────────────────────────────────────────────────────────────────────────────

const ALL_DIALECT_CODES = new Set(
  LANGUAGES.flatMap((l) => l.dialects.map((d) => d.code)),
);

/** Combine language codes and dialect codes into one array for the `languages` column */
export const mergeLanguagesAndDialects = (
  languages: string[],
  dialects: string[],
): string[] => [...languages, ...dialects];

/** Split a raw `languages` array (as stored) back into { languages, dialects } */
export const splitLanguagesAndDialects = (
  raw: string[],
): { languages: string[]; dialects: string[] } => ({
  languages: raw.filter((v) => !ALL_DIALECT_CODES.has(v)),
  dialects: raw.filter((v) => ALL_DIALECT_CODES.has(v)),
});
